/**
 * ChooseTime — the "When would you like it?" picker content.
 *
 * One surface, one decision: ASAP vs Schedule as a segmented toggle (ASAP
 * defined once), with the scheduled day + time revealed inline — no second
 * step, no radio buttons, one commit. Replaces the order-type modal detour
 * for changing the time.
 */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

import SegmentedControl from "~/components/SegmentedControl/SegmentedControl";
import Button from "~/components/Button/Button";
import SiteContext from "~/contexts/SiteContext";
import ThemeContext from "~/contexts/ThemeContext";
import { hasAsap, scheduleDays, timesForDay } from "~/helpers/timeOptions";

import styles from "./ChooseTime.module.css";

type Mode = "ASAP" | "SCHEDULE";

type Props = {
  /** Open flag — used to (re)initialise selection from the current order. */
  visible: boolean;
  availableTimeSlots: (string | null)[];
  /** Current order time: null = ASAP, ISO string = scheduled. */
  requestedReadyTime?: string | null;
  /** ASAP wait in minutes (0 = immediate, null = ASAP not offered). */
  asapWait: number | null;
  saving?: boolean;
  /** null = ASAP, ISO string = the chosen scheduled slot. */
  onSave: (value: string | null) => void;
};

const asapReadout = (asapWait: number | null): string => {
  if (asapWait == null) return "Not available right now";
  if (asapWait === 0) return "As soon as possible";
  return `Ready in about ${asapWait} min`;
};

const ChooseTime: React.FC<Props> = ({
  visible,
  availableTimeSlots,
  requestedReadyTime,
  asapWait,
  saving,
  onSave,
}) => {
  const { site } = useContext(SiteContext);
  const tz = site.timeZone;
  // Filled brand pill for ASAP/Schedule, matching the store picker's segmented
  // control (cohesion). Driven by the org's primary button colour.
  const { template } = useContext(ThemeContext);
  const brandVars = {
    "--seg-active-bg": template.global.button.primaryButtonColour,
    "--seg-active-fg": template.global.button.primaryButtonFontColour,
  } as React.CSSProperties;

  const asapAvailable = hasAsap(availableTimeSlots);
  const days = useMemo(
    () => scheduleDays(availableTimeSlots, tz),
    [availableTimeSlots, tz],
  );
  const scheduleAvailable = days.length > 0;
  const nothingAvailable = !asapAvailable && !scheduleAvailable;

  const [mode, setMode] = useState<Mode>("ASAP");
  const [dayIdx, setDayIdx] = useState(0);
  const [selectedIso, setSelectedIso] = useState<string | null>(null);
  const dayRowRef = useRef<HTMLDivElement>(null);

  // (Re)initialise from the current order each time the picker opens, so it
  // always reflects what's actually set. Kept in one effect to avoid the
  // chained-effect races the old selector had.
  useEffect(() => {
    if (!visible) return;
    if (requestedReadyTime) {
      const idx = days.findIndex((d) =>
        timesForDay(availableTimeSlots, tz, d.date).some(
          (t) => t.iso === requestedReadyTime,
        ),
      );
      setMode("SCHEDULE");
      setDayIdx(idx >= 0 ? idx : 0);
      setSelectedIso(requestedReadyTime);
    } else {
      setMode(asapAvailable ? "ASAP" : "SCHEDULE");
      setDayIdx(0);
      setSelectedIso(null);
    }
    // Only re-init on open, not on every prop tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Guard against a mid-session slot refetch that removes ASAP: don't strand
  // the user on an ASAP mode that no longer exists.
  useEffect(() => {
    if (mode === "ASAP" && !asapAvailable && scheduleAvailable) {
      setMode("SCHEDULE");
    }
  }, [mode, asapAvailable, scheduleAvailable]);

  const times = useMemo(
    () =>
      scheduleAvailable
        ? timesForDay(
            availableTimeSlots,
            tz,
            days[Math.min(dayIdx, days.length - 1)].date,
          )
        : [],
    [availableTimeSlots, tz, days, dayIdx, scheduleAvailable],
  );

  // Keep a valid time selected when the day changes.
  useEffect(() => {
    if (mode !== "SCHEDULE" || times.length === 0) return;
    if (!selectedIso || !times.some((t) => t.iso === selectedIso)) {
      setSelectedIso(times[0].iso);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, dayIdx, times]);

  // Bring the selected day into view — initialising to a day that's scrolled
  // off-screen otherwise looks like an empty row.
  useEffect(() => {
    if (!visible || mode !== "SCHEDULE") return;
    dayRowRef.current
      ?.querySelector<HTMLElement>('[data-selected="true"]')
      ?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [visible, mode, dayIdx]);

  const segmentOptions = [
    ...(asapAvailable ? [{ value: "ASAP", label: "ASAP" }] : []),
    ...(scheduleAvailable ? [{ value: "SCHEDULE", label: "Schedule" }] : []),
  ];

  const canConfirm =
    !saving &&
    !nothingAvailable &&
    (mode === "ASAP" ? asapAvailable : !!selectedIso);

  const handleSave = () => {
    if (!canConfirm) return;
    onSave(mode === "ASAP" ? null : selectedIso);
  };

  // Nothing bookable at all — closed beyond the schedule horizon. Don't leave a
  // dead-end (disabled button, no explanation).
  if (nothingAvailable) {
    return (
      <div className={styles.content}>
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>
            This store isn&apos;t taking orders right now
          </span>
          <span className={styles.emptyHint}>
            It may be closed or fully booked. Try another store, or check back
            soon.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.content, saving && styles.saving)}
      aria-busy={saving}
      style={brandVars}
    >
      {segmentOptions.length > 1 && (
        <SegmentedControl
          options={segmentOptions}
          value={mode}
          onChange={(v) => !saving && setMode(v as Mode)}
        />
      )}

      {/* Explain a missing ASAP option (store closed → pre-order only). */}
      {!asapAvailable && scheduleAvailable && (
        <span className={styles.closedHint}>
          This store is closed right now — pre-order for a later time.
        </span>
      )}

      {mode === "ASAP" ? (
        <div className={styles.asapPanel} key="asap">
          <span className={styles.asapValue}>{asapReadout(asapWait)}</span>
          <span className={styles.asapHint}>
            Your order starts as soon as you pay.
          </span>
        </div>
      ) : (
        <div className={styles.schedulePanel} key="schedule">
          <div className={styles.dayRow} ref={dayRowRef} aria-label="Choose a day">
            {days.map((d, i) => (
              <button
                key={d.date.getTime()}
                type="button"
                aria-pressed={i === dayIdx}
                data-selected={i === dayIdx}
                className={clsx(styles.dayChip, i === dayIdx && styles.daySelected)}
                onClick={() => setDayIdx(i)}
              >
                <span className={styles.dayWeekday}>{d.weekday}</span>
                <span className={styles.dayNum}>{d.dayOfMonth}</span>
              </button>
            ))}
          </div>
          <div className={styles.timeGrid} aria-label="Choose a time">
            {times.map((t) => (
              <button
                key={t.iso}
                type="button"
                aria-pressed={t.iso === selectedIso}
                className={clsx(
                  styles.timeChip,
                  t.iso === selectedIso && styles.timeSelected,
                )}
                onClick={() => setSelectedIso(t.iso)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <Button onClick={handleSave} disabled={!canConfirm}>
        {saving ? "Saving…" : "Set time"}
      </Button>
    </div>
  );
};

export default ChooseTime;
